import React, { useEffect, useState } from 'react';
import { InputNumber, Select } from 'antd';
import { AlignCenter, AlignLeft, AlignRight, Bold, Italic } from 'lucide-react';

import { ToolBtn } from '../../shared/ui/tool-btn';
import { FONT_FAMILY_OPTIONS } from '../../text/constants/fonts';
import { FONT_UI } from '../../constants/design-tokens';
import { PillTextAlign, PillTextbox } from '../types';
import { BOLD_WEIGHT, REGULAR_WEIGHT } from '../constants/defaults';
import {
  setPillFontFamily,
  setPillFontSize,
  setPillFontStyle,
  setPillFontWeight,
  setPillTextAlign
} from '../utils/pill-accessors';
import { usePillContent } from '../hooks/use-pill-content';
import { labelStyle, rowStyle, sectionStyle } from './pill-styles';

type Props = { pill: PillTextbox };

export const PillTypographyRow: React.FC<Props> = ({ pill }) => {
  const { content, patch } = usePillContent(pill);

  const [fontFamily, setFontFamily] = useState('');
  const [fontSize, setFontSize] = useState(0);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [textAlign, setTextAlign] = useState<PillTextAlign>('center');

  useEffect(() => {
    if (!content) return;
    setFontFamily(content.fontFamily);
    setFontSize(content.fontSize);
    setIsBold(Number(content.fontWeight) >= 600);
    setIsItalic(content.fontStyle === 'italic');
    setTextAlign((content.textAlign as PillTextAlign) ?? 'center');
  }, [content]);

  const onFontFamily = (v: string) => {
    setFontFamily(v);
    setPillFontFamily(pill, v);
    patch({ fontFamily: v });
  };

  const onFontSize = (v: number | null) => {
    if (v == null) return;
    setFontSize(v);
    setPillFontSize(pill, v);
    patch({ fontSize: v });
  };

  const toggleBold = () => {
    const next = !isBold;
    setIsBold(next);
    const weight = next ? BOLD_WEIGHT : REGULAR_WEIGHT;
    setPillFontWeight(pill, weight);
    patch({ fontWeight: weight });
  };

  const toggleItalic = () => {
    const next = !isItalic;
    setIsItalic(next);
    const style = next ? 'italic' : 'normal';
    setPillFontStyle(pill, style);
    patch({ fontStyle: style });
  };

  const onAlign = (v: PillTextAlign) => {
    setTextAlign(v);
    setPillTextAlign(pill, v);
    patch({ textAlign: v });
  };

  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>Tipografía</div>
      <div style={{ ...rowStyle, marginBottom: 8 }}>
        <Select
          value={fontFamily}
          onChange={onFontFamily}
          options={FONT_FAMILY_OPTIONS}
          size='small'
          style={{ flex: 1, fontFamily: FONT_UI }}
        />
        <InputNumber
          value={Math.round(fontSize)}
          onChange={onFontSize}
          min={1}
          max={400}
          size='small'
          style={{ width: 70 }}
        />
      </div>
      <div style={rowStyle}>
        <ToolBtn onClick={toggleBold} title='Negrita' active={isBold}>
          <Bold size={15} />
        </ToolBtn>
        <ToolBtn onClick={toggleItalic} title='Itálica' active={isItalic}>
          <Italic size={15} />
        </ToolBtn>
        <ToolBtn onClick={() => onAlign('left')} title='Alinear izquierda' active={textAlign === 'left'}>
          <AlignLeft size={15} />
        </ToolBtn>
        <ToolBtn onClick={() => onAlign('center')} title='Centrar' active={textAlign === 'center'}>
          <AlignCenter size={15} />
        </ToolBtn>
        <ToolBtn onClick={() => onAlign('right')} title='Alinear derecha' active={textAlign === 'right'}>
          <AlignRight size={15} />
        </ToolBtn>
      </div>
    </div>
  );
};
